import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { bankName, businessAddressString } from '../../../shared/config/localeProfile';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../../../shared/types/Invoice';
export function InvoiceDetail() {
  const {
    t
  } = useTranslation('invoices');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data
  const invoice: Invoice = {
    id: id || '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: getTenant().currency.code,
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    notes: 'Thank you for your business!',
    items: [{
      id: '1',
      description: 'Premium Plan Subscription',
      quantity: 1,
      unitPrice: 100000,
      total: 100000
    }, {
      id: '2',
      description: 'Additional User Licenses',
      quantity: 5,
      unitPrice: 5000,
      total: 25000
    }],
    subtotal: 125000,
    taxAmount: 0,
    discountAmount: 0,
    total: 125000
  };
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border-blue-200',
      paid: 'bg-green-50 text-green-700 border-green-200',
      overdue: 'bg-red-50 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-6778">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-6779">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-6780">
        <div className="flex items-center gap-4" data-id="element-6781">
          <button onClick={() => navigate('/invoices')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-6782">
            <ArrowLeftIcon size={18} data-id="element-6783" />
          </button>
          <div data-id="element-6784">
            <div className="flex items-center gap-3" data-id="element-6785">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-6786">
                {invoice.invoiceNumber}
              </h1>
              {getStatusBadge(invoice.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-6787">
              {invoice.customerName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-6788">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-6789">
            <MailIcon size={16} data-id="element-6790" />
            {t('pages.InvoiceDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-6791">
            <DownloadIcon size={16} data-id="element-6792" />
            {t('pages.InvoiceDetail.download')}
          </button>
          <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6793">
            <EditIcon size={16} data-id="element-6794" />
            {t('pages.InvoiceDetail.edit')}
          </button>
          <div className="relative" data-id="element-6795">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-6796">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-6797" />
            </button>
            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-6798" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-6799">
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6800">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-6801" />
                    {t('pages.InvoiceDetail.print')}
                  </button>
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6802">
                    <ArchiveIcon size={16} className="mr-3 text-gray-400" data-id="element-6803" />
                    {t('pages.InvoiceDetail.archive')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-6804" />
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-6805">
                    <TrashIcon size={16} className="mr-3" data-id="element-6806" />
                    {t('pages.InvoiceDetail.delete')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6807">
        {/* Invoice Header */}
        <div className="p-8" data-id="element-6808">
          <div className="flex justify-between items-start mb-8" data-id="element-6809">
            <div data-id="element-6810">
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-6811">
                {t('pages.InvoiceDetail.invoice')}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-6812">{invoice.invoiceNumber}</p>
            </div>
            <div className="text-right" data-id="element-6813">
              <img src={getTenant().brand.logo?.light || getTenant().brand.logo?.dark} alt={bankName()} className="h-10 mb-3 object-contain ml-auto" data-id="element-6814" />

              <p className="text-sm text-gray-600" data-id="element-6815">{bankName()}</p>
              <p className="text-xs text-gray-500" data-id="element-6816">{businessAddressString()}</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-100" data-id="element-6817">
            <div data-id="element-6818">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6819">
                {t('pages.InvoiceDetail.bill_to')}
              </h3>
              <p className="text-sm font-semibold text-gray-900" data-id="element-6820">
                {invoice.customerName}
              </p>
              <p className="text-sm text-gray-600 mt-0.5" data-id="element-6821">
                {invoice.customerEmail}
              </p>
              {invoice.customerPhone && <p className="text-sm text-gray-600 mt-0.5" data-id="element-6822">
                  {invoice.customerPhone}
                </p>}
            </div>
            <div className="text-right space-y-2" data-id="element-6823">
              <div className="flex justify-end gap-4" data-id="element-6824">
                <span className="text-sm text-gray-500" data-id="element-6825">
                  {t('pages.InvoiceDetail.issue_date')}
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-6826">
                  {new Date(invoice.issueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                </span>
              </div>
              <div className="flex justify-end gap-4" data-id="element-6827">
                <span className="text-sm text-gray-500" data-id="element-6828">
                  {t('pages.InvoiceDetail.due_date')}
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-6829">
                  {new Date(invoice.dueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                </span>
              </div>
              {invoice.paidDate && <div className="flex justify-end gap-4" data-id="element-6830">
                  <span className="text-sm text-gray-500" data-id="element-6831">
                    {t('pages.InvoiceDetail.paid_date')}
                  </span>
                  <span className="text-sm font-medium text-green-600" data-id="element-6832">
                    {new Date(invoice.paidDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                  </span>
                </div>}
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8" data-id="element-6833">
            <table className="w-full" data-id="element-6834">
              <thead data-id="element-6835">
                <tr className="border-b border-gray-200" data-id="element-6836">
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6837">
                    {t('pages.InvoiceDetail.description')}
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6838">
                    {t('pages.InvoiceDetail.qty')}
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6839">
                    {t('pages.InvoiceDetail.unit_price')}
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6840">
                    {t('pages.InvoiceDetail.total')}
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-6841">
                {invoice.items.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-6842">
                    <td className="py-3.5 text-sm text-gray-900" data-id="element-6843">
                      {item.description}
                    </td>
                    <td className="py-3.5 text-sm text-gray-900 text-right" data-id="element-6844">
                      {item.quantity}
                    </td>
                    <td className="py-3.5 text-sm text-gray-900 text-right" data-id="element-6845">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {item.unitPrice.toLocaleString('en-US')}
                    </td>
                    <td className="py-3.5 text-sm font-medium text-gray-900 text-right" data-id="element-6846">
                      {t('pages.InvoiceDetail.usd')}{' '}
                      {item.total.toLocaleString('en-US')}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8" data-id="element-6847">
            <div className="w-full max-w-xs space-y-2" data-id="element-6848">
              <div className="flex justify-between py-1.5" data-id="element-6849">
                <span className="text-sm text-gray-500" data-id="element-6850">
                  {t('pages.InvoiceDetail.subtotal')}
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-6851">
                  {t('pages.InvoiceDetail.usd')}{' '}
                  {invoice.subtotal.toLocaleString('en-US')}
                </span>
              </div>
              {!!invoice.discountAmount && invoice.discountAmount > 0 && <div className="flex justify-between py-1.5" data-id="element-6852">
                  <span className="text-sm text-gray-500" data-id="element-6853">
                    {t('pages.InvoiceDetail.discount')}
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-6854">
                    {t('pages.InvoiceDetail.usd_2')}{' '}
                    {invoice.discountAmount.toLocaleString('en-US')}
                  </span>
                </div>}
              {!!invoice.taxAmount && invoice.taxAmount > 0 && <div className="flex justify-between py-1.5" data-id="element-6855">
                  <span className="text-sm text-gray-500" data-id="element-6856">
                    {t('pages.InvoiceDetail.tax')}
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-6857">
                    {t('pages.InvoiceDetail.usd')}{' '}
                    {invoice.taxAmount.toLocaleString('en-US')}
                  </span>
                </div>}
              <div className="flex justify-between py-2.5 border-t-2 border-gray-200" data-id="element-6858">
                <span className="text-base font-semibold text-gray-900" data-id="element-6859">
                  {t('pages.InvoiceDetail.total')}
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-6860">
                  {t('pages.InvoiceDetail.usd')}{' '}
                  {invoice.total.toLocaleString('en-US')}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && <div className="pt-6 border-t border-gray-100" data-id="element-6861">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6862">
                {t('pages.InvoiceDetail.notes')}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-6863">{invoice.notes}</p>
            </div>}
        </div>
      </div>

      {/* Action Cards */}
      {invoice.status === 'sent' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6864">
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-6865">
            <div data-id="element-6866">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-6867">
                {t('pages.InvoiceDetail.mark_as_paid')}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-6868">
                {t('pages.InvoiceDetail.confirm_that_this_invoice_has_been')}
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-6869">
              <CheckCircleIcon size={16} data-id="element-6870" />
              {t('pages.InvoiceDetail.mark_as_paid')}
            </button>
          </div>
        </div>}

      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6871">
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-6872">
            <div data-id="element-6873">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-6874">
                {t('pages.InvoiceDetail.cancel_invoice')}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-6875">
                {t('pages.InvoiceDetail.this_action_cannot_be_undone')}
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors" data-id="element-6876">
              <XCircleIcon size={16} data-id="element-6877" />
              {t('pages.InvoiceDetail.cancel_invoice')}
            </button>
          </div>
        </div>}
    </div>;
}